/** IYB composite score bands (PTSI matrix): higher score = better local compliance. */
export const IYB_SCORE_MAX = 100
/** Scores at or below this value are high local jurisdictional risk (light red). */
export const IYB_LOW_RISK_MAX = 40
export const IYB_MEDIUM_RISK_MAX = 59.99
/** Scores at or above this value are low local risk / fast-track eligible (green). */
export const IYB_HIGH_RISK_MIN = 60

export function resolveIybRiskScore({ compositeScore, indicators } = {}) {
  if (compositeScore != null && !Number.isNaN(Number(compositeScore))) {
    return Number(compositeScore)
  }
  if (!indicators || typeof indicators !== 'object') return null
  const values = Object.values(indicators).filter(v => typeof v === 'number' && !Number.isNaN(v))
  if (!values.length) return null
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * Classify composite IYB score (0–100) into EUDR risk tiers.
 * Green 60–100 → Low Risk | Yellow 40.01–59.99 → Medium Risk | Red 0–40 → High Risk
 */
export function classifyIybRiskScore(score) {
  if (score == null || Number.isNaN(Number(score))) return null
  const s = Number(score)
  if (s >= IYB_HIGH_RISK_MIN) return 'Low Risk'
  if (s > IYB_LOW_RISK_MAX) return 'Medium Risk'
  return 'High Risk'
}

/** Prefer composite-score tier; fall back to stored CSV label when score is missing. */
export function deriveIybRiskStatus({ compositeScore, eudrRiskStatus } = {}) {
  const fromScore = classifyIybRiskScore(compositeScore)
  if (fromScore) return fromScore
  return normalizeRiskStatusLabel(eudrRiskStatus) || null
}

/** Map legacy "Standard Risk" label to the new matrix term. */
export function normalizeRiskStatusLabel(status) {
  if (!status) return status
  if (status === 'Standard Risk') return 'Medium Risk'
  return status
}

const IYB_RISK_LABEL_KEYS = {
  'Low Risk': 'deforestation.combinedComplianceLowRisk',
  'Medium Risk': 'deforestation.combinedComplianceMediumRisk',
  'High Risk': 'deforestation.riskStatusHigh',
  'Critical Risk': 'deforestation.complianceTrafficLightLegendCriticalRisk',
  Zero: 'dueDiligence.iyb.riskLabelZero',
  'N/A': 'dueDiligence.iyb.notAvailable',
}

const IYB_RISK_LABEL_FALLBACKS = {
  'Low Risk': 'Low Risk',
  'Medium Risk': 'Medium Risk',
  'High Risk': 'High Risk',
  'Critical Risk': 'Critical Risk',
  Zero: 'Zero',
  'N/A': 'N/A',
}

/** Translate canonical IYB risk labels for display. Internal logic keeps English keys. */
export function translateIybRiskLabel(label, t) {
  const translate = typeof t === 'function' ? t : null
  const canonical = normalizeRiskStatusLabel(label) || label
  if (!canonical) {
    return translate ? translate('dueDiligence.iyb.notAvailable') : 'N/A'
  }
  const key = IYB_RISK_LABEL_KEYS[canonical]
  if (key && translate) return translate(key)
  return IYB_RISK_LABEL_FALLBACKS[canonical] || canonical
}

export function getIybRiskScoreColor(score) {
  if (score == null) return '#9E9E9E'
  const s = Number(score)
  if (s >= IYB_HIGH_RISK_MIN) return '#489E16'
  if (s > IYB_LOW_RISK_MAX) return '#FFA826'
  return '#DA3D3D'
}

export function getRiskClassForStatus(status) {
  const label = normalizeRiskStatusLabel(status)
  if (label === 'Low Risk') return 'green-button'
  if (label === 'Medium Risk') return 'orange-button'
  if (label === 'High Risk' || label === 'Critical Risk') return 'red-button'
  return 'gray-button'
}

/** High-contrast badge classes for IYB score tables. */
export function getIybRiskBadgeClass(status) {
  const label = normalizeRiskStatusLabel(status)
  if (label === 'Low Risk') return 'iyb-badge iyb-badge--green'
  if (label === 'Medium Risk') return 'iyb-badge iyb-badge--yellow'
  if (label === 'High Risk' || label === 'Critical Risk') return 'iyb-badge iyb-badge--red'
  return 'iyb-badge iyb-badge--gray'
}

export function getIybIndicatorBadgeClass(val) {
  const { badgeClass } = classifyIybIndicatorValue(val)
  if (badgeClass === 'green-button') return 'iyb-badge iyb-badge--green'
  if (badgeClass === 'orange-button') return 'iyb-badge iyb-badge--yellow'
  if (badgeClass === 'red-button') return 'iyb-badge iyb-badge--red'
  return 'iyb-badge iyb-badge--gray'
}

export function getRiskIconForStatus(status) {
  const label = normalizeRiskStatusLabel(status)
  if (label === 'Low Risk') return 'mdi-check-circle'
  if (label === 'Medium Risk') return 'mdi-alert-circle'
  if (label === 'High Risk') return 'mdi-close-circle'
  if (label === 'Critical Risk') return 'mdi-close-octagon'
  return 'mdi-help-circle'
}

/**
 * Classify a 0–100 sustainability indicator value into IYB risk tiers.
 * Higher indicator scores = better performance = lower risk.
 */
export function classifyIybIndicatorValue(val, t) {
  let result
  if (val === null || val === undefined) {
    result = { label: 'N/A', badgeClass: 'gray-button', barColor: '#BDBDBD' }
  } else if (val === 0) {
    result = { label: 'Zero', badgeClass: 'gray-button', barColor: '#BDBDBD' }
  } else if (val >= IYB_HIGH_RISK_MIN) {
    result = { label: 'Low Risk', badgeClass: 'green-button', barColor: '#489E16' }
  } else if (val > IYB_LOW_RISK_MAX) {
    result = { label: 'Medium Risk', badgeClass: 'orange-button', barColor: '#FFA826' }
  } else {
    result = { label: 'High Risk', badgeClass: 'red-button', barColor: '#DA3D3D' }
  }
  if (typeof t === 'function') {
    return { ...result, label: translateIybRiskLabel(result.label, t) }
  }
  return result
}
